"use strict";
import {
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from "@mui/material";
import React, { useEffect, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import Loader from "../Components/Loader";
import axios from "axios";
import { styled } from "@mui/system";
import classes from "../Assets/Styles/Global.module.css";
import { getLanguageName } from "../utils/generalFunctions";
import { GET_SPEECHS_DETAILS_FOR_PROJECT } from "../ApiEndpoints";
import { Store } from "../Store";
import { getProjectById } from "../features/projects/api";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    padding: theme.spacing(1),
    textAlign: "left",
    fontWeight: "bold",
}));

// Format star rating counts into a single string
const renderRatingStars = (ratings) => {
    const labels = ["One", "Two", "Three", "Four", "Five"];
    return labels
        .map((label, index) => {
            const key = label.toLowerCase();
            return `${label} star${index ? "s" : ""}: ${ratings?.[key] ?? 0}`;
        })
        .join(" | ");
};

const ProjectDetailsScreen = () => {
    const { id: projectId } = useParams();
    const [dataLoader, setDataLoader] = useState(false);
    const [projectDetails, setProjectDetails] = useState(null);
    const { dispatch: ctxDispatch } = useContext(Store);

    useEffect(() => {
        // Retrieve project information and statistics
        const fetchData = async () => {
            setDataLoader(true);
            const [{ data: fullProject }, { data: stats }] = await Promise.all([
                getProjectById(projectId),
                axios.post(GET_SPEECHS_DETAILS_FOR_PROJECT, { projectId }),
            ]);

            // Store stats locally and in context
            setProjectDetails(stats);
            const merged = { ...fullProject, stats };
            ctxDispatch({ type: "PROJECT_FILE", payload: merged });
            setDataLoader(false);
        };

        fetchData();
    }, [projectId, ctxDispatch]);

    // Count the number of each emotion present in dialogues
    const countEmotions = (dialogues) => {
        const emotionCount = {};
        dialogues.forEach((dialogue) => {
            const emotion = dialogue.originalAudioEmotion;
            emotionCount[emotion] = (emotionCount[emotion] || 0) + 1;
        });
        return emotionCount;
    };

    // Return the display name for a character, falling back to the speaker name
    const findCharacterByName = (name) => {
        const character = projectDetails.characters.find(
            (character) => character.speaker === name
        );
        return character ? character.displayName || character.speaker : name;
    };

    return (
        <Grid item container justifyContent="center" marginBottom={8}>
            <Loader Load={dataLoader} />

            <Grid item container xs={7} marginTop={7} direction="column">
                <Grid item className={classes["header"]}>
                    Analyze
                </Grid>
                {projectDetails && (
                    <>
                        <Grid item marginTop={2}>
                            <TableContainer component={Paper}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <StyledTableCell>Field</StyledTableCell>
                                            <StyledTableCell>Details</StyledTableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <TableRow>
                                            <StyledTableCell>Number of dialogues</StyledTableCell>
                                            <StyledTableCell>
                                                {projectDetails.numberOfDialogues}
                                            </StyledTableCell>
                                        </TableRow>

                                        <TableRow>
                                            <StyledTableCell>Number of true faces</StyledTableCell>
                                            <StyledTableCell>
                                                {projectDetails.numberOfTrueFaces}
                                            </StyledTableCell>
                                        </TableRow>

                                        <TableRow>
                                            <StyledTableCell>
                                                Number of original dialogues
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {projectDetails.numberOfTrueOriginal}
                                            </StyledTableCell>
                                        </TableRow>

                                        <TableRow>
                                            <StyledTableCell>
                                                Number Of original ratings
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {renderRatingStars(
                                                    projectDetails?.numberOfOriginalRatings
                                                )}
                                            </StyledTableCell>
                                        </TableRow>

                                        {projectDetails?.numberOfratingsTranslations?.map(
                                            (rating, index) => (
                                                <TableRow key={index}>
                                                    <StyledTableCell>
                                                        Number Of original ratings of {" "}
                                                        {getLanguageName(rating.language)}
                                                    </StyledTableCell>
                                                    <StyledTableCell>
                                                        {renderRatingStars(rating?.ratings)}
                                                    </StyledTableCell>
                                                </TableRow>
                                            )
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>

                        <Grid item marginTop={2}>
                            <TableContainer component={Paper}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <StyledTableCell>Characters</StyledTableCell>
                                            <StyledTableCell>+4 rating</StyledTableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {projectDetails?.charactersStats &&
                                            Object.keys(projectDetails.charactersStats).map(
                                                (char, index) => {
                                                    const dialogues = Object.values(
                                                        projectDetails.charactersStats[char].dialogues
                                                    );
                                                    const emotionCounts = countEmotions(dialogues);
                                                    const displayName = findCharacterByName(char);

                                                    return (
                                                        <TableRow key={index}>
                                                            <StyledTableCell>
                                                                {displayName}
                                                            </StyledTableCell>
                                                            <StyledTableCell>
                                                                {Object.keys(emotionCounts).map(
                                                                    (emotion, idx) => (
                                                                        <span key={idx}>
                                                                            {emotion}: {emotionCounts[emotion]}{" "}
                                                                        </span>
                                                                    )
                                                                )}
                                                            </StyledTableCell>
                                                        </TableRow>
                                                    );
                                                }
                                            )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </>
                )}
            </Grid>
        </Grid>
    );
};

export default ProjectDetailsScreen;
